import { Injectable } from '@angular/core';
import { ClubTheme } from 'src/app/shared/models/club/club.model';
import { PageBuilderPage } from 'src/app/shared/models/content/page-builder/page-builder.model';
import {
    getBackgroundBlendModeMap,
    getBackgroundFitMap,
    getBackgroundGradientDirectionMap,
    getBackgroundPositionMap,
    getBackgroundRepeatMap,
    hexToRgba,
} from 'src/app/shared/constants';

/** The page-builder color picker's "no color" value. */
const NO_COLOR = '#ffffff0';

const IMAGE_PROPERTIES = [
    '--html-background-image',
    '--html-background-position',
    '--html-background-attachment',
    '--html-background-size',
    '--html-background-repeat',
    '--html-background-blend-mode',
];

interface BackgroundFields {
    color: string | null | undefined;
    imageUrl: string | null | undefined;
    positionID: number | null | undefined;
    attachmentID: number | null | undefined;
    fitID: number | null | undefined;
    repeatID: number | null | undefined;
    blendModeID: number | null | undefined;
    gradientDirectionID: number | null | undefined;
    overlayTypeID: number | null | undefined;
    overlayColor: string | null | undefined;
    overlayOpacity: string | null | undefined;
    secondaryOverlayColor: string | null | undefined;
    secondaryOverlayOpacity: string | null | undefined;
    /** The theme only layers its overlay over an image; a page's overlay also sits over a plain color. */
    overlayWithoutImage: boolean;
}

export function hasPageBackground(page: PageBuilderPage | null | undefined): boolean {
    return !!page && ((!!page.backgroundColor && page.backgroundColor !== NO_COLOR) || !!page.backgroundImageUrl);
}

function themeFields(theme: ClubTheme): BackgroundFields {
    return {
        color: theme.pageBackgroundColor,
        imageUrl: theme.pageBackgroundImageUrl,
        positionID: theme.pageBackgroundPositionID,
        attachmentID: theme.pageBackgroundImageAttachmentID,
        fitID: theme.pageBackgroundImageFitID,
        repeatID: theme.pageBackgroundRepeatID,
        blendModeID: theme.pageBackgroundBlendModeID,
        gradientDirectionID: theme.pageBackgroundGradientDirectionID,
        overlayTypeID: theme.pageBackgroundOverlayTypeID,
        overlayColor: theme.pageOverlayColor,
        overlayOpacity: theme.pageOverlayOpacity,
        secondaryOverlayColor: theme.pageSecondaryOverlayColor,
        secondaryOverlayOpacity: theme.pageSecondaryOverlayOpacity,
        overlayWithoutImage: false,
    };
}

function pageFields(page: PageBuilderPage): BackgroundFields {
    return {
        color: page.backgroundColor,
        imageUrl: page.backgroundImageUrl,
        positionID: page.backgroundPositionID,
        attachmentID: page.backgroundImageAttachmentID,
        fitID: page.backgroundImageFitID,
        repeatID: page.backgroundRepeatID,
        blendModeID: page.backgroundBlendModeID,
        gradientDirectionID: page.backgroundGradientDirectionID,
        overlayTypeID: page.backgroundOverlayTypeID,
        overlayColor: page.overlayColor,
        overlayOpacity: page.overlayOpacity,
        secondaryOverlayColor: page.secondaryOverlayColor,
        secondaryOverlayOpacity: page.secondaryOverlayOpacity,
        overlayWithoutImage: true,
    };
}

/**
 * Owns the `--html-background-*` variables behind the body background. The club theme (applied by
 * the header) and a CMS page's own background (applied by the page view) arrive asynchronously, and
 * the page's wins whenever it is set, whichever load finishes last.
 */
@Injectable({ providedIn: 'root' })
export class HtmlBackgroundService {
    private theme: ClubTheme | null = null;
    private page: PageBuilderPage | null = null;

    applyTheme(theme: ClubTheme | null | undefined): void {
        this.theme = theme ?? null;
        this.render();
    }

    /** A page without a color or image of its own clears the previous page's override. */
    setPageOverride(page: PageBuilderPage | null | undefined): void {
        this.page = hasPageBackground(page) ? page : null;
        this.render();
    }

    clearPageOverride(): void {
        this.setPageOverride(null);
    }

    private render(): void {
        const root = document.documentElement.style;
        const fields = this.page ? pageFields(this.page) : this.theme ? themeFields(this.theme) : null;

        if (!fields) {
            root.removeProperty('--html-background-color');
            IMAGE_PROPERTIES.forEach((property) => root.removeProperty(property));
            return;
        }

        root.setProperty('--html-background-color', fields.color || 'transparent');

        if (!fields.imageUrl && !fields.overlayWithoutImage) {
            IMAGE_PROPERTIES.forEach((property) => root.removeProperty(property));
            return;
        }

        const overlayTypeID = fields.overlayTypeID || 1;
        const overlayColor = fields.overlayColor && overlayTypeID !== 1
            ? hexToRgba(fields.overlayColor, parseFloat(fields.overlayOpacity || '0'))
            : 'transparent';
        const secondaryOverlayColor = fields.secondaryOverlayColor && overlayTypeID === 3
            ? hexToRgba(fields.secondaryOverlayColor, parseFloat(fields.secondaryOverlayOpacity || '0'))
            : overlayColor;
        const overlayGradient = `linear-gradient(${getBackgroundGradientDirectionMap(fields.gradientDirectionID)}, ${secondaryOverlayColor} 0%, ${overlayColor} 100%)`;
        const image = fields.imageUrl ? `url(${fields.imageUrl})` : 'none';

        root.setProperty('--html-background-image', `${overlayGradient}, ${image}`);
        root.setProperty('--html-background-position', `0 0, ${getBackgroundPositionMap(fields.positionID)}`);
        root.setProperty('--html-background-attachment', fields.attachmentID === 1 ? 'fixed' : 'scroll');
        root.setProperty('--html-background-size', `cover, ${getBackgroundFitMap(fields.fitID)}`);
        root.setProperty('--html-background-repeat', `no-repeat, ${getBackgroundRepeatMap(fields.repeatID)}`);
        root.setProperty('--html-background-blend-mode', `${getBackgroundBlendModeMap(fields.blendModeID)}, normal`);
    }
}
